import { create } from "zustand";
import type { Account, Subscription } from "@/types";
import { authService } from "@/lib/auth/authService";
import { setWahooUserId } from "@/lib/integrations/wahooAuth";

type AuthStore = {
  account: Account | null;
  subscription: Subscription | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * True once `initializeAuth()` has resolved.
   * The app renders a loading screen while this is false so users never see
   * a flash of the login page when they already have a valid session.
   */
  _hasHydrated: boolean;

  // Actions
  setAccount: (account: Account | null) => void;
  setSubscription: (subscription: Subscription | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => Promise<void>;

  /**
   * Must be called once on app mount (in App.tsx useEffect).
   *
   * 1. Calls supabase.auth.getSession() — populates account/subscription if a
   *    valid session already exists in localStorage (returning user).
   * 2. Registers onAuthStateChange — keeps the store in sync with every
   *    future Supabase event (sign-in, sign-out, token refresh).
   * 3. Always sets _hasHydrated = true in the finally block so the loading
   *    screen resolves regardless of network conditions.
   *
   * Returns a cleanup function — pass it back from the useEffect return so
   * the listener is removed when the component unmounts (avoids memory leaks
   * in React Strict Mode's double-invoke).
   */
  initializeAuth: () => Promise<() => void>;
};

export const useAuthStore = create<AuthStore>()((set) => ({
  account: null,
  subscription: null,
  isAuthenticated: false,
  isLoading: false,
  _hasHydrated: false,

  setAccount: (account) => set({ account, isAuthenticated: !!account }),

  setSubscription: (subscription) => set({ subscription }),

  setLoading: (loading) => set({ isLoading: loading }),

  logout: async () => {
    // Sign out from Supabase first so the JWT is invalidated on the server.
    // Then clear local state regardless of whether the API call succeeds.
    try {
      await authService.signOut();
    } catch (err) {
      console.error("[authStore] signOut failed:", err);
    }
    set({ account: null, subscription: null, isAuthenticated: false });
  },

  initializeAuth: async () => {
    const isResetPage = window.location.pathname === "/reset-password";

    try {
      set({ isLoading: true });

      if (!isResetPage) {
        const sessionData = await authService.getSession();
        if (sessionData) {
          setWahooUserId(sessionData.account.id);
          set({
            account: sessionData.account,
            subscription: sessionData.subscription,
            isAuthenticated: true,
          });
        }
      }
    } finally {
      set({ _hasHydrated: true, isLoading: false });
    }

    // Register a live listener so the store stays in sync after the initial
    // check — handles sign-in from another tab, token expiry, etc.
    // On the reset-password page we suppress SIGNED_IN events to prevent
    // Supabase's auto-login from navigating the user away mid-reset.
    const unsubscribe = authService.onAuthStateChange((payload) => {
      if (payload) {
        if (isResetPage) return; // ignore SIGNED_IN during password reset flow
        setWahooUserId(payload.account.id);
        set({
          account: payload.account,
          subscription: payload.subscription,
          isAuthenticated: true,
        });
      } else {
        setWahooUserId(null);
        set({
          account: null,
          subscription: null,
          isAuthenticated: false,
        });
      }
    });

    return unsubscribe;
  },
}));
